<w-page [mode]="PageMode.Page" [headerSize]="$headerSize()" [hideFooter]="true" [class.tabs-hidden]="!$showTabStrip()" [class.tabs-flush]="!$tabStripPadding()" class="frame-manage" styleClass="surface-manage-frame">

  <ng-container *w-page-header-title>
    <div class="manage-identity">
      @if ($showBreadcrumbs() && $breadcrumbs().length > 0) {
        <nav class="manage-breadcrumb" aria-label="Breadcrumb">
          @for (crumb of $breadcrumbs(); track $index; let last = $last; let count = $count) {
            @if (last) {
              <ng-container [ngTemplateOutlet]="identifierTitle" />
            } @else if (crumb.routerLink) {
              <a class="crumb crumb-link" [routerLink]="crumb.routerLink" (click)="onBreadcrumbClick($event, crumb.routerLink, $index, count)">{{ crumb.label }}</a>
            } @else {
              <span class="crumb">{{ crumb.label }}</span>
            }
            @if (!last) {
              <span class="crumb-sep" aria-hidden="true">/</span>
            }
          }
        </nav>
      } @else {
        @if ($kind(); as kind) {
          <span class="manage-kind">{{ kind }}</span>
        }
        <ng-container [ngTemplateOutlet]="identifierTitle" />
      }
      @for (tag of $headerTags(); track tag.key) {
        @if (tag.style) {
          <w-tag [label]="tag.label" mode="squared" [size]="headerTagSize" [color]="tag.style.color" [backgroundColor]="tag.style.backgroundColor" [borderColor]="tag.style.borderColor" [tooltip]="tag.tooltip" />
        } @else {
          <w-tag [label]="tag.label" mode="squared" [size]="headerTagSize" [tooltip]="tag.tooltip" />
        }
      }
    </div>
  </ng-container>

  <ng-container *w-page-header-center>
    @if ($headerChips().length > 0) {
      <div class="manage-header-chips" aria-label="Record information">
        @for (chip of $headerChips(); track chip.key) {
          <div class="manage-header-chip">
            <span class="manage-header-chip-label">{{ chip.label }}</span>
            @if (chip.style) {
              <w-tag class="manage-header-chip-value" [label]="chip.value" mode="squared" [size]="headerTagSize" [color]="chip.style.color" [backgroundColor]="chip.style.backgroundColor" [borderColor]="chip.style.borderColor" />
            } @else {
              <w-tag class="manage-header-chip-value" [label]="chip.value" mode="squared" [size]="headerTagSize" />
            }
          </div>
        }
      </div>
    }
  </ng-container>

  <ng-container *w-page-header-actions>
    <div class="manage-header-actions">
      @if ($saveStatusLabel(); as saveLabel) {
        <span class="manage-save-status" [attr.data-status]="$saveStatus()" role="status" aria-live="polite" (animate.enter)="onSaveStatusEnter($event)" (animate.leave)="onSaveStatusLeave($event)">
          @if ($saveStatus() === 'saving') {
            <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
          } @else if ($saveStatus() === 'error') {
            <i class="fa-regular fa-circle-exclamation" aria-hidden="true"></i>
          } @else if ($saveStatus() === 'saved') {
            <i class="fa-regular fa-check" aria-hidden="true"></i>
          } @else if ($saveStatus() === 'dirty') {
            <span class="manage-save-status-dot" aria-hidden="true"></span>
          }
          <span class="manage-save-status-label">{{ saveLabel }}</span>
        </span>
      }
      @if ($headerActionCommands().length > 0) {
        <wb-action-bar [commands]="$headerActionCommands()" />
      }
      @if ($canOpenManageCommands()) {
        <wb-button-icon class="manage-more-actions" iconLeft="fa-regular fa-ellipsis" displayStyle="Secondary" size="Small" [tooltip]="moreActionsTooltip" (onClick)="openManageCommands()" />
      }
      <!-- Last, so capability controls sit beside w-page's close. -->
      <wb-surface-capability-region [fields]="$capabilityFields()" [surfaceMode]="$surfaceMode()" />
    </div>
  </ng-container>

  <!-- Banner region under the header (alert strips). Mounted from the optional
       `banner` capability; absent → nothing renders. -->
  @if ($banner(); as banner) {
    <ng-container *w-page-alert>
      <ng-container [ngComponentOutlet]="banner.component" [ngComponentOutletInputs]="banner.inputs?.() ?? {}" />
    </ng-container>
  }

  <!-- Optional entity rail, otherwise summary sections (display-only unless `.editable()`). -->
  @if ($hasLeftColumn()) {
    <ng-container *w-page-body-left-content>
      <div class="manage-left-column" [class.collapsed]="$leftColumnCollapsedView()">
        @if (!$leftColumnCollapsedView()) {
          @if ($leftRail(); as rail) {
            <aside class="manage-left-rail" [class.resizing]="$leftRailResizing()">
              <ng-container [ngComponentOutlet]="rail.component" [ngComponentOutletInputs]="$leftRailInputs()" />
              <div class="left-rail-resize-handle" aria-hidden="true" (pointerdown)="onLeftRailResizeStart($event)" (pointermove)="onLeftRailResizeMove($event)" (pointerup)="onLeftRailResizeEnd($event)" (pointercancel)="onLeftRailResizeEnd($event)" (dblclick)="onLeftRailResizeReset()"></div>
            </aside>
          } @else {
            <aside class="manage-summary">
              @for (item of $summary(); track item.section.path; let first = $first) {
                @if (item.spacer) {
                  <div class="section-spacer" [class.is-gap]="item.section.layout === SectionLayout.Gap" aria-hidden="true"></div>
                } @else {
                  <wb-section
                    [path]="item.section.path"
                    [title]="item.$title()"
                    [columns]="item.section.columns"
                    [layout]="item.section.layout"
                    density="summary"
                    [readonly]="$page().section(item.section.path).$readonly()"
                    [fields]="item.fields"
                    [fieldGroups]="item.fieldGroups"
                    [related]="item.section.related"
                    [repeats]="item.repeats"
                    [resolver]="item.resolver"
                    [customRenderer]="item.customRenderer"
                    [customRendererInputs]="item.customRendererInputs"
                    [surfaceMode]="$surfaceMode()"
                    [page]="$page()"
                  >
                    @if (first && $collapseSummary()) {
                      <div wbSectionHeaderEnd>
                        <wb-button-icon iconLeft="fa-regular fa-table-columns" displayStyle="Tertiary" size="Small" tooltip="Hide summary" (onClick)="toggleLeftColumn()" />
                      </div>
                    }
                  </wb-section>
                }
              }
            </aside>
          }
        }
        @if ($leftColumnCollapsedView()) {
          <div class="manage-left-column-toggle">
            <wb-button-icon iconLeft="fa-regular fa-table-columns" displayStyle="Tertiary" size="Small" tooltip="Show summary" (onClick)="toggleLeftColumn()" />
          </div>
        }
      </div>
    </ng-container>
  }

  <!-- Tab strip in page-body-header (legacy entity-manage placement): sticky,
       outside the body scroll so only tab content scrolls. Panels render in
       the body via the shell's $activeTab (renderPanels=false keeps empty ARIA
       panels out of the strip). Save status text lives in the page header only;
       dirty which-tab cues are dots on tab names (out of flow). -->
  <ng-container *w-page-body-header>
    @if ($showTabStrip()) {
      <wb-tab-shell class="manage-tabs" [tabs]="$tabDeclarations()" [selectedTab]="$selectedTab()" [renderPanels]="false" (selectedTabChange)="onSelectedTabChange($event)" />
    }
  </ng-container>

  <!-- Body content. Relation boards mount here under .page-body (relative +
       remaining height). entity-dashboard uses display:contents + absolute
       w-page inset:0 against that box — legacy entity-form-manage pattern. -->
  <ng-container *w-page-body-content>
    @if ($surfaceErrors().length > 0) {
      <div class="manage-form-errors" role="alert">
        @for (err of $surfaceErrors(); track err.code) {
          <p class="manage-form-error-line">{{ err.message }}</p>
        }
      </div>
    }

    <!-- formDefinition tabs stay mounted so pending child blur saves survive outer tab switches.
         Same manage-tab-sections / canvas chrome as form tabs so Details gets card-section bg. -->
    @for (info of $formDefinitionTabs(); track info.tab.path) {
      @if (info.formDefinition; as formDefinition) {
        <div class="manage-tab-sections" [hidden]="$activeTabInfo()?.tab.path !== info.tab.path">
          <div class="manage-tab-canvas">
            <div class="manage-tab-content" [style.max-width]="$contentMaxWidth()">
              <wb-form-definition-tab-host
                [tabPath]="info.tab.path"
                [definition]="formDefinition.definition()"
                [active]="$activeTabInfo()?.tab.path === info.tab.path"
              />
            </div>
          </div>
        </div>
      }
    }

    @if ($activeTabInfo(); as info) {
      @switch (info.tab.type) {

        @case ('relation') {
          <div class="manage-tab-relation">
            @if (info.mount; as mount) {
              <ng-container [ngComponentOutlet]="mount.component" [ngComponentOutletInputs]="mount.inputs()" [ngComponentOutletInjector]="mount.injector" />
            } @else {
              <div class="manage-tab-placeholder">{{ $page().tab(info.tab.path).$title() }}</div>
            }
          </div>
        }

        @case ('relationGroup') {
          <div class="manage-tab-relation">
            <wb-relation-group [segments]="info.segments ?? []" [param]="info.tab.param ?? info.tab.path" [memoryKey]="relationGroupMemoryKey($relationGroupMemoryOwner(), info.tab.path)" [persistUrl]="$relationGroupPersistUrl()" />
          </div>
        }

        @case ('custom') {
          @if (info.custom; as custom) {
            <ng-container [ngComponentOutlet]="custom.component" [ngComponentOutletInputs]="custom.inputs()" />
          } @else {
            <div class="manage-tab-placeholder">{{ $page().tab(info.tab.path).$title() }}</div>
          }
        }

        @case ('formDefinition') {
          <!-- Content rendered by the persistent formDefinition host above. -->
        }

        @default {
          <div class="manage-tab-sections">
            <div class="manage-tab-canvas">
              <div class="manage-tab-content" [style.max-width]="$contentMaxWidth()">
                @for (band of info.layout; track $index) {
                  @if (band.type === 'columns') {
                    <div class="section-columns" [style.grid-template-columns]="'repeat(' + band.columns.length + ', minmax(0, 1fr))'">
                      @for (column of band.columns; track $index) {
                        <div class="section-column">
                          @for (item of column; track item.section.path) {
                            @if (item.spacer) {
                              <div class="section-spacer" [class.is-gap]="item.section.layout === SectionLayout.Gap" aria-hidden="true"></div>
                            } @else if (item.section.related) {
                              <!-- Related owns its own wb-panel (title + header actions). -->
                              <wb-section [path]="item.section.path" [title]="item.$title()" [columns]="item.section.columns" [layout]="item.section.layout" [readonly]="$page().section(item.section.path).$readonly()" [fields]="item.fields" [fieldGroups]="item.fieldGroups" [related]="item.section.related" [repeats]="item.repeats" [resolver]="item.resolver" [customRenderer]="item.customRenderer" [customRendererInputs]="item.customRendererInputs" [surfaceMode]="$surfaceMode()" [page]="$page()" />
                            } @else {
                              <wb-panel variant="section" [title]="item.$title()" [collapsible]="item.section.collapsible">
                                <wb-section [path]="item.section.path" [columns]="item.section.columns" [layout]="item.section.layout" [readonly]="$page().section(item.section.path).$readonly()" [fields]="item.fields" [fieldGroups]="item.fieldGroups" [repeats]="item.repeats" [resolver]="item.resolver" [customRenderer]="item.customRenderer" [customRendererInputs]="item.customRendererInputs" [surfaceMode]="$surfaceMode()" [page]="$page()" />
                              </wb-panel>
                            }
                          }
                        </div>
                      }
                    </div>
                  } @else {
                    @if (band.section.spacer) {
                      <div class="section-spacer" [class.is-gap]="band.section.section.layout === SectionLayout.Gap" aria-hidden="true"></div>
                    } @else if (band.section.section.related) {
                      <wb-section [path]="band.section.section.path" [title]="band.section.$title()" [columns]="band.section.section.columns" [layout]="band.section.section.layout" [readonly]="$page().section(band.section.section.path).$readonly()" [fields]="band.section.fields" [fieldGroups]="band.section.fieldGroups" [related]="band.section.section.related" [repeats]="band.section.repeats" [resolver]="band.section.resolver" [customRenderer]="band.section.customRenderer" [customRendererInputs]="band.section.customRendererInputs" [surfaceMode]="$surfaceMode()" [page]="$page()" />
                    } @else {
                      <wb-panel variant="section" [title]="band.section.$title()" [collapsible]="band.section.section.collapsible">
                        <wb-section [path]="band.section.section.path" [columns]="band.section.section.columns" [layout]="band.section.section.layout" [readonly]="$page().section(band.section.section.path).$readonly()" [fields]="band.section.fields" [fieldGroups]="band.section.fieldGroups" [repeats]="band.section.repeats" [resolver]="band.section.resolver" [customRenderer]="band.section.customRenderer" [customRendererInputs]="band.section.customRendererInputs" [surfaceMode]="$surfaceMode()" [page]="$page()" />
                      </wb-panel>
                    }
                  }
                }
              </div>
            </div>
          </div>
        }
      }
    }
  </ng-container>

</w-page>

<ng-template #identifierTitle>
  @if ($identifierEditor(); as editor) {
    <div class="manage-title is-editable">
      <wb-field
        class="manage-title-field"
        [field]="editor.field"
        [metadata]="editor.metadata"
        [instance]="editor.instance"
        [page]="$page()"
        [surfaceMode]="$surfaceMode()"
        density="summary"
      />
      @if (!$page().$readonly() && !(editor.instance ? editor.instance.$readonly() : editor.field.$readonly())) {
        <i class="fa-regular fa-pen manage-title-edit" aria-hidden="true"></i>
      }
    </div>
  } @else {
    <h2 class="manage-title">{{ $identifierLabel() }}</h2>
  }
</ng-template>
